import React, { useState, useRef, useEffect } from 'react';
import {
  Bell,
  BellOff,
  CheckCheck,
  Heart,
  MessageSquare,
  Repeat,
  Trophy,
  User,
  UserPlus,
} from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';
import { formatRelativeTime } from '../services/notificationService';
import type { NotificationEventType, NotificationResourceType } from '../types';
import '../notifications.css';

export interface NotificationPopoverProps {
  currentUserId?: string | null;
  onNavigateToResource?: (resourceType: NotificationResourceType, resourceId: string) => void;
}

export const NotificationPopover: React.FC<NotificationPopoverProps> = ({
  currentUserId,
  onNavigateToResource,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    notifications,
    unreadCount,
    isLoading,
    isMarkingAllRead,
    refreshNotifications,
    markAsRead,
    markAllAsRead,
  } = useNotifications(currentUserId);

  // Close on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const toggleOpen = () => {
    if (!isOpen) {
      refreshNotifications();
    }
    setIsOpen((prev) => !prev);
  };

  const handleItemClick = async (
    notificationId: string,
    isRead: boolean,
    resourceType: NotificationResourceType,
    resourceId: string
  ) => {
    if (!isRead) {
      await markAsRead(notificationId);
    }
    setIsOpen(false);
    if (onNavigateToResource) {
      onNavigateToResource(resourceType, resourceId);
    }
  };

  const renderEventIcon = (eventType: NotificationEventType) => {
    switch (eventType) {
      case 'comment_created':
        return <MessageSquare size={10} />;
      case 'meme_upvoted':
        return <Heart size={10} />;
      case 'user_followed':
        return <UserPlus size={10} />;
      case 'meme_remixed':
        return <Repeat size={10} />;
      case 'contest_winner':
        return <Trophy size={10} />;
      default:
        return <Bell size={10} />;
    }
  };

  if (!currentUserId) {
    return null;
  }

  return (
    <div className="notification-container" ref={containerRef}>
      <button
        type="button"
        className="notification-trigger"
        onClick={toggleOpen}
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={isOpen}
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className="notification-popover"
          role="region"
          aria-label="Notifications menu"
          data-testid="notification-popover"
        >
          <div className="notification-header">
            <h3>Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                className="notification-mark-all-btn"
                onClick={markAllAsRead}
                disabled={isMarkingAllRead}
              >
                <CheckCheck size={14} style={{ display: 'inline', marginRight: '4px' }} />
                Mark all as read
              </button>
            )}
          </div>

          <div className="notification-list" role="list">
            {isLoading && notifications.length === 0 ? (
              <div className="notification-loading" data-testid="notification-loading">
                <p style={{ fontSize: '0.8125rem', color: '#64748b' }}>Loading notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="notification-empty" data-testid="notification-empty">
                <BellOff size={32} style={{ opacity: 0.5 }} />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((item) => (
                <div
                  key={item.id}
                  className={`notification-item ${!item.is_read ? 'unread' : ''}`}
                  onClick={() =>
                    handleItemClick(item.id, item.is_read, item.resource_type, item.resource_id)
                  }
                  role="listitem"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleItemClick(
                        item.id,
                        item.is_read,
                        item.resource_type,
                        item.resource_id
                      );
                    }
                  }}
                  data-testid={`notification-item-${item.id}`}
                >
                  <div className="notification-avatar-wrap">
                    {item.actor?.avatarUrl ? (
                      <img
                        src={item.actor.avatarUrl}
                        alt={item.actor.displayName || 'User'}
                        className="notification-avatar"
                      />
                    ) : (
                      <div className="notification-avatar">
                        <User size={18} style={{ color: '#64748b' }} />
                      </div>
                    )}
                    <span className="notification-type-badge">
                      {renderEventIcon(item.event_type)}
                    </span>
                  </div>

                  <div className="notification-content">
                    <div className="notification-text">{item.message}</div>
                    <div className="notification-meta">
                      <span>{formatRelativeTime(item.created_at)}</span>
                    </div>
                  </div>

                  {!item.is_read && <span className="notification-unread-dot" />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
